import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Dialog, EmptyState, Field, Icon, IconButton, Select, TextArea, useLayout, useToast } from "@agentsaas/ui";
import { ApiError, type Actor, type Task } from "@agentsaas/protocol";
import { useErrorText, useT } from "../app/i18n";
import { useServices } from "../app/services";
import { useSession } from "../app/session";
import { formatDateTime } from "../app/format";
import { useAgents } from "../data/queries";
import { ck, putTask, usePulls, useTask, useTasks, type ObservedStatus } from "../data/collab";
import { Loading } from "../layout/Loading";
import { Markdown } from "../chat/Markdown";
import { AgentStatusDot } from "../agent/TeamList";
import { CollabFrame } from "../project/ProjectChrome";
import { NotFound } from "../pages/NotFound";
import { AcceptBadge, TaskStatusBadge } from "./TasksPage";
import { mergeHistory, taskActions } from "./model";
import { AssetRefList, collectAssetRefs } from "../assets/AssetRef";
import { stripRefLines } from "../assets/refs";
import { parseAssetRef } from "../rooms/model";

/** /p/:pid/tasks/:tkid：任务详情 + 分派 / 验收 / 返工 / 取消 */
export function TaskDetailPage() {
  const { pid = "", tkid = "" } = useParams();
  return (
    <CollabFrame pid={pid} section="tasks">
      <TaskDetail pid={pid} tkid={tkid} />
    </CollabFrame>
  );
}

function useObservedHistory(tkid: string) {
  return useQuery<ObservedStatus[]>({ queryKey: ck.taskHistory(tkid), queryFn: () => [], staleTime: Infinity, gcTime: Infinity }).data ?? [];
}

function TaskDetail({ pid, tkid }: { pid: string; tkid: string }) {
  const { t, locale } = useT();
  const mobile = useLayout() === "mobile";
  const tz = useSession().state.user?.timezone;
  const navigate = useNavigate();
  const q = useTask(tkid);
  const all = useTasks(pid);
  const agents = useAgents(pid);
  const pulls = usePulls(pid);
  const observed = useObservedHistory(tkid);
  if (q.isPending) return <Loading />;
  if (q.isError || !q.data) return <NotFound />;
  const task = q.data;
  const agentOf = (id: string | null) => agents.data?.find((a) => a.id === id);
  const assignee = agentOf(task.assignee);
  const pr = pulls.data?.find((p) => p.taskId === task.id) ?? (task.prNumber ? pulls.data?.find((p) => p.number === task.prNumber) : undefined);
  const parent = all.data?.find((x) => x.id === task.parentId);
  const children = all.data?.filter((x) => x.parentId === task.id) ?? [];
  const refs = task.referenceTaskIds.map((id) => all.data?.find((x) => x.id === id) ?? ({ id, title: id } as Pick<Task, "id" | "title">));
  const reworks = all.data?.filter((x) => x.referenceTaskIds.includes(task.id)) ?? [];
  const when = (iso: string) => formatDateTime(iso, locale, tz);
  const actor = (a: Actor) => (a.type === "agent" ? (agentOf(a.id)?.name ?? a.id) : a.type === "user" ? t("task.by_user") : t("task.by_system"));

  return (
    <div className="page task-detail" data-testid="task-detail" data-status={task.status}>
      <div className="task-detail__back">
        {mobile ? (
          <IconButton icon="back" label={t("common.back")} onClick={() => navigate(`/p/${pid}/tasks`)} data-testid="task-back" />
        ) : (
          <Link to={`/p/${pid}/tasks`} className="small">
            {t("task.back_to_list")}
          </Link>
        )}
      </div>
      <header className="task-detail__head">
        <h1 className="page__title">{task.title}</h1>
        <div className="row">
          <TaskStatusBadge task={task} />
          <AcceptBadge task={task} />
          {task.blockedReason ? <Badge tone="warning">{task.blockedReason}</Badge> : null}
        </div>
      </header>
      <div className="task-detail__grid">
        <div className="stack task-detail__main">
          <TaskActions task={task} agents={agents.data ?? []} />
          <section className="card">
            <h2 className="section-title">{t("task.field.spec")}</h2>
            {task.spec ? <Markdown text={stripRefLines(task.spec)} /> : <p className="muted small">{t("task.no_spec")}</p>}
            <AssetRefList pid={pid} refs={collectAssetRefs([], task.spec)} />
          </section>
          <section className="card">
            <h2 className="section-title">{t("task.artifacts")}</h2>
            {task.artifacts.length ? (
              <>
                <ul className="refs" data-testid="task-artifacts">
                  {task.artifacts
                    .filter((r) => !parseAssetRef(r))
                    .map((r) => (
                      <li key={r}>
                        <code>{r}</code>
                      </li>
                    ))}
                </ul>
                <AssetRefList pid={pid} refs={collectAssetRefs(task.artifacts)} />
              </>
            ) : (
              <p className="muted small">{t("task.no_artifacts")}</p>
            )}
          </section>
          <section className="card">
            <h2 className="section-title">{t("task.history")}</h2>
            <ol className="timeline" data-testid="task-history">
              {mergeHistory(task, observed).map((h) => (
                <li key={`${h.status}@${h.at}`}>
                  <span className="timeline__dot" aria-hidden="true" />
                  <span className="grow">{h.status === "confirmed" ? t("task.accepted") : t(`task.status.${h.status}`)}</span>
                  <time className="muted small" dateTime={h.at}>
                    {when(h.at)}
                  </time>
                </li>
              ))}
            </ol>
          </section>
        </div>
        <aside className="card task-detail__side">
          <dl className="props">
            <dt>{t("task.field.assignee")}</dt>
            <dd data-testid="task-assignee-name">
              {assignee ? (
                <Link to={`/p/${pid}/a/${assignee.id}`} className="row-inline">
                  <AgentStatusDot status={assignee.status} />
                  {assignee.name}
                </Link>
              ) : (
                <span className="muted">{t("task.unassigned")}</span>
              )}
            </dd>
            <dt>{t("task.branch")}</dt>
            <dd>{task.branch ? <code className="break">{task.branch}</code> : <span className="muted">{t("task.none")}</span>}</dd>
            <dt>{t("task.pr")}</dt>
            <dd>
              {pr ? (
                <Link to={`/p/${pid}/pulls/${pr.repoId}/${pr.number}`} data-testid="task-pr-link">
                  <Icon name="pr" size={14} /> {t("pr.number", { n: pr.number })} {pr.title}
                </Link>
              ) : task.prNumber ? (
                t("pr.number", { n: task.prNumber })
              ) : (
                <span className="muted">{t("task.none")}</span>
              )}
              {pr ? (
                <div>
                  <Badge tone={pr.state === "merged" ? "success" : pr.state === "open" ? "accent" : "neutral"}>{t(`pr.state.${pr.state}`)}</Badge>
                </div>
              ) : null}
            </dd>
            <dt>{t("task.field.parent")}</dt>
            <dd>{parent ? <Link to={`/p/${pid}/tasks/${parent.id}`}>{parent.title}</Link> : <span className="muted">{t("task.none")}</span>}</dd>
            {children.length ? (
              <>
                <dt>{t("task.children")}</dt>
                <dd>
                  <ul className="plain">
                    {children.map((c) => (
                      <li key={c.id}>
                        <Link to={`/p/${pid}/tasks/${c.id}`}>{c.title}</Link> <TaskStatusBadge task={c} />
                      </li>
                    ))}
                  </ul>
                </dd>
              </>
            ) : null}
            {refs.length ? (
              <>
                <dt>{t("task.rework_of")}</dt>
                <dd>
                  <ul className="plain">
                    {refs.map((r) => (
                      <li key={r.id}>
                        <Link to={`/p/${pid}/tasks/${r.id}`}>{r.title}</Link>
                      </li>
                    ))}
                  </ul>
                </dd>
              </>
            ) : null}
            {reworks.length ? (
              <>
                <dt>{t("task.reworked_as")}</dt>
                <dd>
                  <ul className="plain">
                    {reworks.map((r) => (
                      <li key={r.id}>
                        <Link to={`/p/${pid}/tasks/${r.id}`}>{r.title}</Link> <TaskStatusBadge task={r} />
                      </li>
                    ))}
                  </ul>
                </dd>
              </>
            ) : null}
            <dt>{t("task.created_by")}</dt>
            <dd>
              {actor(task.createdBy)}
              <div className="muted small">{when(task.createdAt)}</div>
            </dd>
          </dl>
        </aside>
      </div>
    </div>
  );
}

/** 人对任务的操作：分派（改派）、验收、返工、取消 */
function TaskActions({ task, agents }: { task: Task; agents: { id: string; name: string }[] }) {
  const { t } = useT();
  const errText = useErrorText();
  const toast = useToast();
  const { api } = useServices();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const can = taskActions(task);
  const [picked, setAssignee] = useState<string | null>(null);
  const assignee = picked ?? task.assignee ?? agents[0]?.id ?? "";
  const [busy, setBusy] = useState<string | null>(null);
  const [rework, setRework] = useState(false);
  const [cancel, setCancel] = useState(false);
  const [feedback, setFeedback] = useState("");

  const act = async (action: "assign" | "confirm" | "rework" | "cancel", body: { assignee?: string; feedback?: string } = {}) => {
    setBusy(action);
    try {
      const out = await api.call("taskAction", { params: { tkid: task.id, action }, body });
      putTask(qc, out);
      toast.show({ message: t(`task.done.${action}`, { title: out.title }), tone: "success", duration: 2500 });
      if (action === "rework") {
        setRework(false);
        setFeedback("");
        navigate(`/p/${task.projectId}/tasks/${out.id}`);
      }
      if (action === "cancel") setCancel(false);
    } catch (e) {
      toast.show({ message: errText(e), tone: "danger" });
      if (e instanceof ApiError && e.code === "task.invalid_transition") void qc.invalidateQueries({ queryKey: ck.task(task.id) });
    } finally {
      setBusy(null);
    }
  };

  if (!can.assign && !can.confirm && !can.rework && !can.cancel) return null;
  return (
    <section className="card task-actions" aria-label={t("task.actions")} data-testid="task-actions">
      {can.confirm ? (
        <div className="task-actions__row">
          <p className="grow small">{t("task.confirm_hint")}</p>
          <Button variant="primary" icon="check" loading={busy === "confirm"} disabled={!!busy} onClick={() => void act("confirm")} data-testid="task-confirm">
            {t("task.confirm")}
          </Button>
        </div>
      ) : null}
      {can.assign ? (
        <div className="task-actions__row">
          <Field label={task.assignee ? t("task.reassign") : t("task.assign")}>
            {(id) =>
              agents.length ? (
                <Select id={id} value={assignee} onChange={(e) => setAssignee(e.target.value)} data-testid="task-assignee">
                  {agents.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </Select>
              ) : (
                <EmptyState title={t("agent.empty")} />
              )
            }
          </Field>
          <Button
            variant={task.assignee ? "secondary" : "primary"}
            loading={busy === "assign"}
            disabled={!!busy || !assignee || assignee === task.assignee}
            onClick={() => void act("assign", { assignee })}
            data-testid="task-assign"
          >
            {task.assignee ? t("task.reassign") : t("task.assign")}
          </Button>
        </div>
      ) : null}
      <div className="row task-actions__more">
        {can.rework ? (
          <Button icon="refresh" disabled={!!busy} onClick={() => setRework(true)} data-testid="task-rework">
            {t("task.rework")}
          </Button>
        ) : null}
        {can.cancel ? (
          <Button variant="ghost" disabled={!!busy} onClick={() => setCancel(true)} data-testid="task-cancel">
            {t("task.cancel")}
          </Button>
        ) : null}
      </div>
      <Dialog
        open={rework}
        onClose={() => setRework(false)}
        title={t("task.rework")}
        closeLabel={t("shell.close")}
        footer={
          <>
            <Button onClick={() => setRework(false)}>{t("common.cancel")}</Button>
            <Button variant="primary" loading={busy === "rework"} disabled={!feedback.trim()} onClick={() => void act("rework", { feedback })} data-testid="rework-submit">
              {t("task.rework_submit")}
            </Button>
          </>
        }
      >
        <p className="small muted">{t("task.rework_hint")}</p>
        <Field label={t("task.feedback")}>
          {(id) => <TextArea id={id} data-autofocus rows={5} value={feedback} onChange={(e) => setFeedback(e.target.value)} data-testid="rework-feedback" />}
        </Field>
      </Dialog>
      <Dialog
        open={cancel}
        onClose={() => setCancel(false)}
        title={t("task.cancel")}
        closeLabel={t("shell.close")}
        size="sm"
        footer={
          <>
            <Button onClick={() => setCancel(false)}>{t("common.back")}</Button>
            <Button variant="danger" loading={busy === "cancel"} onClick={() => void act("cancel")} data-testid="cancel-confirm">
              {t("task.cancel")}
            </Button>
          </>
        }
      >
        <p>{t("task.cancel_confirm", { title: task.title })}</p>
      </Dialog>
    </section>
  );
}
